import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { colors, spacing, typography } from '../theme';
import { Input, PhoneInput, Button, TimezoneInput } from './design-system';
import { useAppStore } from '../store/appStore';
import { apiService } from '../services/api';
import { parseInternationalPhoneNumber } from '../utils/phoneNumber';
import { TimezoneService } from '../services/timezone.service';
import { useAppContext } from '../contexts/AppContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { defaultPhoneCountry } from '../utils/businessDefaults';

interface PersonalInfoSettingsProps {
  onBack?: () => void;
}

export const PersonalInfoSettings: React.FC<PersonalInfoSettingsProps> = ({ onBack }) => {
  const styles = useThemedStyles(getStyles);
  const { user, setUser } = useAppStore();
  const { showNotification } = useAppContext();
  const insets = useSafeAreaInsets();
  const [isLoading, setIsLoading] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(user?.profilePicture || user?.avatar || null);
  const [profileImageUri, setProfileImageUri] = useState<string | null>(null); // Local URI for upload
  const phoneInputRef = useRef<any>(null);
  const isSavingRef = useRef<boolean>(false); // Track if we're in the middle of saving
  const lastLoadedAvatarRef = useRef<string | null>(null); // Track last avatar URL we loaded to prevent loops
  const lastProcessedUserAvatarRef = useRef<string | null>(null); // Track last user avatar we processed in useEffect
  const imageLoadAttemptedRef = useRef<Set<string>>(new Set()); // Track which image URLs we've already attempted to load to prevent retry loops
  const [formData, setFormData] = useState({
    name: user?.name || user?.firstName && user?.lastName 
      ? `${user.firstName} ${user.lastName}`.trim() 
      : '',
    phone: user?.phone || user?.mobile || '',
    email: user?.email || '',
    timezone: user?.timezone || '',
    timezoneOffset: user?.timezoneOffset || '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const hasLoadedFreshProfile = useRef<boolean>(false); // Track if we've loaded fresh profile on mount
  const isMountedRef = useRef<boolean>(true); // Track if component is mounted

  // Fetch fresh profile data when component mounts
  // BUT: Don't clear profileImageUri if user is in the middle of selecting an image
  useEffect(() => {
    const loadFreshProfile = async () => {
      // Skip if already loaded or if user has a local image selected
      if (hasLoadedFreshProfile.current || profileImageUri) {
        console.log('🔄 [PersonalInfoSettings] Skipping fresh profile load -', 
          hasLoadedFreshProfile.current ? 'already loaded' : 'user has local image selected');
        return;
      }
      
      // Prevent multiple simultaneous loads
      if (isLoadingProfile) {
        console.log('🔄 [PersonalInfoSettings] Already loading profile, skipping...');
        return;
      }
      
      try {
        setIsLoadingProfile(true);
        hasLoadedFreshProfile.current = true; // Mark as loaded
        console.log('🔄 [PersonalInfoSettings] Loading fresh profile data from API...');
        
        const response = await apiService.getUserProfile();
        
        if (response.statusCode === 1000 && response.data) {
          const profileData = response.data;
          const freshAvatarUrl = profileData.avatar || profileData.profilePicture || null;
          
          console.log('✅ [PersonalInfoSettings] Fresh profile data loaded:', {
            firstName: profileData.firstName,
            lastName: profileData.lastName,
            mobile: profileData.mobile,
            avatar: freshAvatarUrl ? freshAvatarUrl.substring(0, 50) + '...' : 'null/empty'
          });
          
          // CRITICAL: Check if this is the same URL we already loaded to prevent infinite loops
          const isSameAsLastLoaded = freshAvatarUrl === lastLoadedAvatarRef.current;
          
          // CRITICAL: Only update if avatar URL is actually different from what we have
          // AND if it's not empty/null (to prevent setting deleted URLs)
          const currentAvatar = user?.avatar || user?.profilePicture;
          const isValidAvatar = freshAvatarUrl && freshAvatarUrl.trim() !== '' && freshAvatarUrl !== 'null' && freshAvatarUrl !== 'undefined';
          const shouldUpdateAvatar = isValidAvatar && 
                                     freshAvatarUrl !== currentAvatar && 
                                     !isSameAsLastLoaded;
          
          console.log('🔍 [PersonalInfoSettings] Avatar update check:', {
            freshAvatarUrl: freshAvatarUrl ? freshAvatarUrl.substring(0, 50) + '...' : 'null',
            currentAvatar: currentAvatar ? currentAvatar.substring(0, 50) + '...' : 'null',
            lastLoaded: lastLoadedAvatarRef.current ? lastLoadedAvatarRef.current.substring(0, 50) + '...' : 'null',
            isValidAvatar,
            shouldUpdateAvatar,
            isSameAsLastLoaded
          });
          
          // Normalize the response data
          const normalizedUser = {
            ...profileData,
            id: profileData.id || profileData._id,
            mobile: profileData.mobile || profileData.phone,
            phone: profileData.phone || profileData.mobile,
            // Set avatar to null/empty if it's invalid, otherwise use the fresh URL
            avatar: isValidAvatar ? freshAvatarUrl : (profileData.avatar || null),
            profilePicture: isValidAvatar ? freshAvatarUrl : (profileData.profilePicture || profileData.avatar || null),
            timezone: profileData.timezone,
            timezoneOffset: profileData.timezoneOffset,
          };
          
          // Remove _id if it exists
          if ((normalizedUser as any)._id && normalizedUser.id) {
            delete (normalizedUser as any)._id;
          }
          
          // Only update store if avatar actually changed to prevent infinite loops
          const avatarChanged = (normalizedUser.avatar || normalizedUser.profilePicture) !== (user?.avatar || user?.profilePicture);
          
          if (setUser && avatarChanged && isMountedRef.current) {
            setUser(normalizedUser);
            console.log('✅ [PersonalInfoSettings] Updated user store with fresh profile data');
            // Update the ref to track what we just loaded
            lastLoadedAvatarRef.current = freshAvatarUrl || null;
          } else if (!avatarChanged) {
            console.log('🔄 [PersonalInfoSettings] Avatar unchanged, skipping store update to prevent loop');
          }
          
          // IMPORTANT: Update profileImage state with the fresh avatar
          // But only if it's valid and different from current
          if (shouldUpdateAvatar && !profileImageUri && isMountedRef.current) {
            console.log('🔄 [PersonalInfoSettings] Updating profileImage from fresh profile load:', freshAvatarUrl.substring(0, 50) + '...');
            setProfileImage(freshAvatarUrl);
            lastLoadedAvatarRef.current = freshAvatarUrl;
          } else if (!isValidAvatar && profileImage && isMountedRef.current) {
            // Clear profileImage if avatar was deleted from backend
            console.log('🔄 [PersonalInfoSettings] Avatar is empty/null in backend, clearing profileImage');
            setProfileImage(null);
            lastLoadedAvatarRef.current = null;
          } else if (isSameAsLastLoaded) {
            console.log('🔄 [PersonalInfoSettings] Same avatar as last load, skipping update to prevent loop');
          }
        } else {
          console.warn('⚠️ [PersonalInfoSettings] Failed to load fresh profile:', response);
        }
      } catch (error) {
        console.error('❌ [PersonalInfoSettings] Error loading fresh profile:', error);
        hasLoadedFreshProfile.current = false; // Reset on error to allow retry
      } finally {
        if (isMountedRef.current) {
          setIsLoadingProfile(false);
        }
      }
    };
    
    // Load fresh data when component mounts
    if (isMountedRef.current) {
      loadFreshProfile();
    }
    
    // Cleanup on unmount
    return () => {
      isMountedRef.current = false;
    };
  }, []); // Empty dependency array - only run on mount

  useEffect(() => {
    // Update form data when user changes
    if (user && isMountedRef.current) {
      setFormData({
        name: user?.name || (user?.firstName && user?.lastName 
          ? `${user.firstName} ${user.lastName}`.trim() 
          : ''),
        phone: user?.phone || user?.mobile || '',
        email: user?.email || '',
        timezone: user?.timezone || '',
        timezoneOffset: user?.timezoneOffset || '',
      });
      
      // Only update profileImage if we don't have a local image being edited
      // This prevents the useEffect from overwriting the selected image before save
      const userAvatar = user?.profilePicture || user?.avatar || null;
      
      // CRITICAL: Check if this is the same avatar we just processed to prevent infinite loops
      const isSameAsLastProcessed = userAvatar === lastProcessedUserAvatarRef.current;
      
      // CRITICAL: Check if avatar is empty/null/whitespace (deleted from backend)
      const isAvatarEmpty = !userAvatar || userAvatar.trim() === '' || userAvatar === 'null' || userAvatar === 'undefined';
      
      console.log('🔍 [PersonalInfoSettings] useEffect [user] triggered:', {
        userAvatar: userAvatar ? userAvatar.substring(0, 50) + '...' : 'null',
        lastProcessed: lastProcessedUserAvatarRef.current ? lastProcessedUserAvatarRef.current.substring(0, 50) + '...' : 'null',
        isSameAsLastProcessed,
        isAvatarEmpty,
        hasProfileImageUri: !!profileImageUri,
        isSaving: isSavingRef.current,
        isLoadingProfile
      });
      
      // Only update if:
      // 1. We don't have a local image URI (not currently editing)
      // 2. We're not in the middle of saving (prevents clearing image after save)
      // 3. We're not currently loading fresh profile (prevents race conditions)
      // 4. The avatar is actually different from what we last processed
      // 5. The user avatar is different from current profileImage
      if (!profileImageUri && !isSavingRef.current && !isLoadingProfile && isMountedRef.current && !isSameAsLastProcessed) {
        // Check if current profileImage is a valid server URL that we want to keep
        const isCurrentImageServerUrl = profileImage && (profileImage.startsWith('http://') || profileImage.startsWith('https://'));
        
        if (isAvatarEmpty && profileImage) {
          // Avatar was deleted from backend, clear the image
          console.log('🔄 [PersonalInfoSettings] Avatar deleted from backend, clearing profileImage');
          setProfileImage(null);
          lastProcessedUserAvatarRef.current = null;
          lastLoadedAvatarRef.current = null;
        } else if (userAvatar && userAvatar.trim() !== '' && userAvatar !== profileImage) {
          // Only update if the new URL is actually different and valid (not empty)
          // Don't overwrite a valid server URL with the same URL
          console.log('🔄 [PersonalInfoSettings] Updating profileImage from user store:', {
            oldProfileImage: profileImage ? profileImage.substring(0, 50) + '...' : null,
            newUserAvatar: userAvatar.substring(0, 50) + '...',
            isCurrentServerUrl: isCurrentImageServerUrl
          });
          setProfileImage(userAvatar);
          lastProcessedUserAvatarRef.current = userAvatar;
          console.log('✅ [PersonalInfoSettings] Updated profile image from user');
        } else if ((!userAvatar || userAvatar.trim() === '') && profileImage && !isCurrentImageServerUrl) {
          // Only clear if user has no avatar AND current image is not a server URL
          // This prevents clearing a valid server URL that just got saved
          console.log('🔄 [PersonalInfoSettings] Clearing profile image - user has no avatar and image is not server URL');
          setProfileImage(null);
          lastLoadedAvatarRef.current = null;
          lastProcessedUserAvatarRef.current = null;
        } else {
          console.log('🔄 [PersonalInfoSettings] No profileImage update needed:', {
            hasUserAvatar: !!userAvatar && !isAvatarEmpty,
            userAvatarMatches: userAvatar === profileImage,
            currentProfileImage: profileImage ? profileImage.substring(0, 50) + '...' : null,
            isServerUrl: isCurrentImageServerUrl,
            isSaving: isSavingRef.current,
            isLoadingProfile: isLoadingProfile,
            hasProfileImageUri: !!profileImageUri,
            isSameAsLastProcessed
          });
          // Still update the ref even if we don't change the image
          lastProcessedUserAvatarRef.current = userAvatar;
        }
      } else {
        const reasons = [];
        if (profileImageUri) reasons.push('local image being edited');
        if (isSavingRef.current) reasons.push('saving in progress');
        if (isLoadingProfile) reasons.push('loading profile');
        if (isSameAsLastProcessed) reasons.push('same as last processed');
        console.log(`🔄 [PersonalInfoSettings] Skipping profile image update - ${reasons.join(', ')}`);
      }
      
      // Parse phone number and set country code and national number
      if (user?.mobile && phoneInputRef.current) {
        try {
          // The phone is stored as (code)(phone) format like "201234567890"
          // We need to parse it to extract country code and national number
          const storedPhone = user.mobile.replace(/[^\d+]/g, '');
          
          // Ensure it has + prefix for parsing
          const phoneToParse = storedPhone.startsWith('+') ? storedPhone : `+${storedPhone}`;
          
          // Parse the phone number
          const parsed = parseInternationalPhoneNumber(phoneToParse);
          
          if (parsed && parsed.country) {
            // Set the country in PhoneInput
            phoneInputRef.current.setCountry(parsed.country);
            
            // Set only the national number (without country code) in the input
            // This will be displayed correctly with the country code prefix
            const nationalNumber = parsed.nationalNumber || '';
            setFormData(prev => ({
              ...prev,
              phone: nationalNumber
            }));
            
            console.log('✅ [PersonalInfoSettings] Parsed phone:', {
              original: user.mobile,
              country: parsed.country,
              nationalNumber: nationalNumber,
              countryCode: parsed.countryCode
            });
          } else {
            // Fallback: try to detect country from common patterns
            const phone = storedPhone;
            if (phone.startsWith('20')) {
              phoneInputRef.current.setCountry(defaultPhoneCountry());
              setFormData(prev => ({ ...prev, phone: phone.substring(2) }));
            } else if (phone.startsWith('966')) {
              phoneInputRef.current.setCountry('SA');
              setFormData(prev => ({ ...prev, phone: phone.substring(3) }));
            } else if (phone.startsWith('1')) {
              phoneInputRef.current.setCountry('US');
              setFormData(prev => ({ ...prev, phone: phone.substring(1) }));
            }
          }
        } catch (error) {
          console.error('❌ [PersonalInfoSettings] Error parsing phone number:', error);
          // If parsing fails, just set the phone as-is
        }
      }
    }
  }, [user]);

  // Debug: Log profileImage changes and clear attempted set when URL changes
  useEffect(() => {
    if (Platform.OS === 'web') {
      console.log('🔄 [PersonalInfoSettings] profileImage changed:', {
        profileImage: profileImage ? profileImage.substring(0, 100) + '...' : 'null',
        profileImageLength: profileImage?.length || 0,
        profileImageUri: profileImageUri ? profileImageUri.substring(0, 50) + '...' : 'null',
        isSaving: isSavingRef.current,
        isLoadingProfile
      });
      
      // Clear old URLs from attempted set when profileImage changes to allow retry with new URL
      // Keep only the current URL if it's in the set
      if (profileImage) {
        const currentUrl = profileImage;
        const attemptedUrls = Array.from(imageLoadAttemptedRef.current);
        imageLoadAttemptedRef.current.clear();
        // Only re-add current URL if it was previously attempted
        if (attemptedUrls.includes(currentUrl)) {
          imageLoadAttemptedRef.current.add(currentUrl);
        }
      } else {
        // Clear all if no image
        imageLoadAttemptedRef.current.clear();
      }
    }
  }, [profileImage, profileImageUri, isLoadingProfile]);

  const handlePickImage = async () => {
    try {
      console.log('📷 [PersonalInfoSettings] Starting image picker...');
      
      // Request permissions
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      console.log('📷 [PersonalInfoSettings] Permission status:', status);
      
      if (status !== 'granted') {
        showNotification('warning', 'Permission Required', 'Please grant camera roll permissions to change your profile picture.');
        return;
      }

      // Launch image picker
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      console.log('📷 [PersonalInfoSettings] Image picker result:', { 
        canceled: result.canceled, 
        hasAssets: !!result.assets?.[0],
        uri: result.assets?.[0]?.uri?.substring(0, 50) + '...' 
      });

      if (!result.canceled && result.assets[0]) {
        const asset = result.assets[0];
        console.log('📷 [PersonalInfoSettings] Image selected:', { 
          uri: asset.uri.substring(0, 50) + '...',
          uriType: asset.uri.substring(0, 10), // blob:, data:, file:, etc.
          width: asset.width,
          height: asset.height,
          type: asset.type
        });
        
        setProfileImageUri(asset.uri); // Store local URI for upload
        setProfileImage(asset.uri); // Update preview immediately
        
        console.log('✅ [PersonalInfoSettings] Image state updated. Will upload when Save is clicked.');
      } else {
        console.log('⚠️ [PersonalInfoSettings] Image picker was canceled or no asset selected');
      }
    } catch (error) {
      console.error('❌ [PersonalInfoSettings] Error picking image:', error);
      showNotification('error', 'Error', 'Failed to pick image. Please try again.');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    isSavingRef.current = true; // Mark that we're saving
    console.log('💾 [PersonalInfoSettings] Save button clicked', {
      hasProfileImageUri: !!profileImageUri,
      profileImageUri: profileImageUri ? profileImageUri.substring(0, 100) : null,
      profileImage: profileImage ? profileImage.substring(0, 100) : null
    });
    
    if (!validateForm()) {
      console.log('❌ [PersonalInfoSettings] Form validation failed');
      return;
    }

    console.log('✅ [PersonalInfoSettings] Form validation passed, starting save...');
    setIsLoading(true);
    
    try {
      // Split name into first and last name
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Get phone number from PhoneInput component
      let formattedPhone = formData.phone;
      let phoneWithCountryCode = '';
      
      if (phoneInputRef.current) {
        const phoneData = phoneInputRef.current.getValue();
        if (phoneData?.formattedNumber) {
          // Format as (countryCode)(phoneNumber) - e.g., "201234567890"
          const countryCode = phoneInputRef.current.getCountryCode(); // Returns "20", "966", etc.
          const nationalNumber = phoneData.nationalNumber || phoneData.formattedNumber.replace(/[^\d]/g, '');
          
          // Remove country code from national number if it's already included
          let cleanNumber = nationalNumber;
          if (nationalNumber.startsWith(countryCode)) {
            cleanNumber = nationalNumber.substring(countryCode.length);
          }
          
          // Format as (code)(phone) - e.g., "201234567890"
          phoneWithCountryCode = `${countryCode}${cleanNumber}`;
          formattedPhone = phoneWithCountryCode;
        }
      }

      // If profile image changed, upload it first
      let avatarUrl = user?.avatar || user?.profilePicture;
      
      // Upload if we have a local image URI (file://, content://, ph://, blob:, data:)
      // This indicates a newly selected image that needs to be uploaded
      const isLocalImage = profileImageUri && (
        profileImageUri.startsWith('file://') || 
        profileImageUri.startsWith('content://') || 
        profileImageUri.startsWith('ph://') || 
        profileImageUri.startsWith('blob:') || 
        profileImageUri.startsWith('data:')
      );
      
      console.log('🔍 [PersonalInfoSettings] Checking if image needs upload:', {
        hasProfileImageUri: !!profileImageUri,
        profileImageUri: profileImageUri ? profileImageUri.substring(0, 100) : null,
        profileImageUriType: profileImageUri ? profileImageUri.substring(0, 10) : null,
        currentAvatar: user?.avatar,
        currentProfilePicture: user?.profilePicture,
        isLocalImage,
        willUpload: !!isLocalImage
      });
      
      if (isLocalImage) {
        console.log('📤 [PersonalInfoSettings] Image changed, starting upload...');
        try {
          // Extract file name and type from URI
          let fileName = profileImageUri.split('/').pop() || `profile-${Date.now()}`;
          
          // Detect file type from URI extension or MIME type
          let fileType = 'image/jpeg'; // Default
          const uriLower = profileImageUri.toLowerCase();
          
          // Check extension first
          if (uriLower.includes('.png') || uriLower.endsWith('.png')) {
            fileType = 'image/png';
            if (!fileName.toLowerCase().endsWith('.png')) {
              fileName = fileName.split('.')[0] + '.png';
            }
          } else if (uriLower.includes('.gif') || uriLower.endsWith('.gif')) {
            fileType = 'image/gif';
            if (!fileName.toLowerCase().endsWith('.gif')) {
              fileName = fileName.split('.')[0] + '.gif';
            }
          } else if (uriLower.includes('.webp') || uriLower.endsWith('.webp')) {
            fileType = 'image/webp';
            if (!fileName.toLowerCase().endsWith('.webp')) {
              fileName = fileName.split('.')[0] + '.webp';
            }
          } else if (uriLower.includes('.jpeg') || uriLower.endsWith('.jpeg')) {
            fileType = 'image/jpeg';
            if (!fileName.toLowerCase().endsWith('.jpeg') && !fileName.toLowerCase().endsWith('.jpg')) {
              fileName = fileName.split('.')[0] + '.jpg';
            }
          } else if (uriLower.includes('.jpg') || uriLower.endsWith('.jpg')) {
            fileType = 'image/jpeg';
            if (!fileName.toLowerCase().endsWith('.jpg')) {
              fileName = fileName.split('.')[0] + '.jpg';
            }
          } else {
            // Default to jpg if no extension found
            fileType = 'image/jpeg';
            if (!fileName.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/)) {
              fileName = fileName.split('.')[0] + '.jpg';
            }
          }
          
          console.log('📤 [PersonalInfoSettings] Uploading profile picture:', { fileName, fileType, uri: profileImageUri.substring(0, 50) + '...' });
          
          const uploadResponse = await apiService.uploadProfilePicture(profileImageUri, fileName, fileType);
          
          console.log('📥 [PersonalInfoSettings] Upload response:', { 
            statusCode: uploadResponse.statusCode, 
            hasData: !!uploadResponse.data,
            dataType: typeof uploadResponse.data,
            dataKeys: uploadResponse.data && typeof uploadResponse.data === 'object' ? Object.keys(uploadResponse.data) : [],
            fullResponse: JSON.stringify(uploadResponse).substring(0, 200)
          });
          
          // Check multiple possible response structures
          if (uploadResponse.statusCode === 1000) {
            // Try different possible response structures
            let uploadedAvatarUrl: string | null = null;
            
            const responseData = uploadResponse.data;
            
            // Check if data is a string (direct URL)
            if (typeof responseData === 'string') {
              uploadedAvatarUrl = responseData;
            }
            // Check if data is an object with avatar property
            else if (responseData && typeof responseData === 'object') {
              // The backend returns the full user object with avatar, profilePicture, mediaUrl, etc.
              const data = responseData as any; // Cast to any to handle dynamic response structure
              uploadedAvatarUrl = data.avatar || 
                                 data.profilePicture ||
                                 data.mediaUrl ||
                                 data.url ||
                                 null;
            }
            
            console.log('🔍 [PersonalInfoSettings] Extracted avatar URL from upload response:', {
              uploadedAvatarUrl: uploadedAvatarUrl ? uploadedAvatarUrl.substring(0, 80) + '...' : null,
              responseDataType: typeof responseData,
              responseDataKeys: responseData && typeof responseData === 'object' ? Object.keys(responseData) : []
            });
            
            if (uploadedAvatarUrl) {
              avatarUrl = uploadedAvatarUrl;
              console.log('✅ [PersonalInfoSettings] Profile picture uploaded successfully, avatar URL:', avatarUrl.substring(0, 80) + '...');
              
              // IMMEDIATELY update profileImage with the uploaded URL so it displays
              // This is critical - set it right away before any other state updates
              console.log('🔄 [PersonalInfoSettings] Setting profileImage to uploaded URL immediately');
              setProfileImage(uploadedAvatarUrl);
              
              // Update refs to track this as the last loaded/processed avatar
              lastLoadedAvatarRef.current = uploadedAvatarUrl;
              lastProcessedUserAvatarRef.current = uploadedAvatarUrl;
              
              // Clear profileImageUri since we now have the server URL
              // This prevents the useEffect from interfering
              setProfileImageUri(null);
              
              console.log('✅ [PersonalInfoSettings] Updated profileImage and cleared profileImageUri');
            } else {
              console.error('❌ [PersonalInfoSettings] Upload succeeded but no avatar URL in response.', {
                responseData: responseData,
                responseDataType: typeof responseData,
                fullResponse: JSON.stringify(uploadResponse).substring(0, 300)
              });
              showNotification('warning', 'Warning', 'Profile picture upload succeeded but URL not received. Please try again.');
              // Don't proceed with update if upload failed to get URL
              setIsLoading(false);
              isSavingRef.current = false;
              return;
            }
          } else {
            console.error('❌ [PersonalInfoSettings] Failed to upload profile picture:', uploadResponse);
            showNotification('warning', 'Warning', uploadResponse.errors?.[0]?.message || 'Profile picture upload failed, but other changes will be saved.');
            // Continue with update even if upload failed (user might want to save other fields)
          }
        } catch (uploadError: any) {
          console.error('❌ [PersonalInfoSettings] Error uploading profile picture:', uploadError);
          showNotification('warning', 'Warning', 'Profile picture upload failed, but other changes will be saved.');
        }
      }

      // Prepare update data
      const updateData: any = {
        firstName,
        lastName,
        mobile: formattedPhone, // Send in (code)(phone) format
      };

      // Always include avatar URL if it exists (either existing or newly uploaded)
      if (avatarUrl) {
        updateData.avatar = avatarUrl;
        console.log('✅ [PersonalInfoSettings] Including avatar in update payload:', avatarUrl);
      } else {
        console.log('⚠️ [PersonalInfoSettings] No avatar URL to include in update payload');
      }

      // Include timezone if it exists (even if empty, to allow clearing)
      // Always include if it's different from the user's current timezone
      const currentTimezone = user?.timezone || '';
      const currentTimezoneOffset = user?.timezoneOffset || '';
      const timezoneChanged = formData.timezone !== currentTimezone || formData.timezoneOffset !== currentTimezoneOffset;
      
      if (formData.timezone || timezoneChanged) {
        updateData.timezone = formData.timezone || null;
        updateData.timezoneOffset = formData.timezoneOffset || null;
        console.log('✅ [PersonalInfoSettings] Including timezone in update payload:', {
          timezone: formData.timezone,
          timezoneOffset: formData.timezoneOffset,
          timezoneChanged,
          currentTimezone,
          currentTimezoneOffset
        });
      } else {
        console.log('🔄 [PersonalInfoSettings] Timezone unchanged, skipping:', {
          formDataTimezone: formData.timezone,
          currentTimezone
        });
      }

      console.log('📤 [PersonalInfoSettings] Sending update payload:', {
        firstName: updateData.firstName,
        lastName: updateData.lastName,
        mobile: updateData.mobile,
        hasAvatar: !!updateData.avatar,
        avatar: updateData.avatar ? updateData.avatar.substring(0, 50) + '...' : null,
        hasTimezone: !!updateData.timezone,
        timezone: updateData.timezone,
        timezoneOffset: updateData.timezoneOffset
      });

      // Call API to update user profile
      const response = await apiService.updateProfile(updateData);

      if (response.statusCode === 1000 && response.data) {
        // Update local user state with response data
        if (setUser) {
          const responseData = response.data;
          // Use avatar from response if available, otherwise fall back to the uploaded URL
          const finalAvatarUrl = responseData.avatar || responseData.profilePicture || avatarUrl;
          
          console.log('💾 [PersonalInfoSettings] Updating user state with:', {
            responseAvatar: responseData.avatar,
            responseProfilePicture: responseData.profilePicture,
            uploadedAvatarUrl: avatarUrl,
            finalAvatarUrl
          });
          
          const updatedUser = {
            ...user,
            ...responseData,
            id: responseData.id || responseData._id || user?.id,
            name: formData.name,
            firstName,
            lastName,
            phone: formattedPhone,
            mobile: formattedPhone,
            profilePicture: finalAvatarUrl,
            avatar: finalAvatarUrl,
            timezone: formData.timezone || responseData.timezone,
            timezoneOffset: formData.timezoneOffset || responseData.timezoneOffset,
          };
          
          setUser(updatedUser);
          
          // Update local profile image state to match the saved URL
          if (finalAvatarUrl) {
            console.log('🔄 [PersonalInfoSettings] Setting profileImage to finalAvatarUrl:', finalAvatarUrl);
            setProfileImage(finalAvatarUrl);
            lastLoadedAvatarRef.current = finalAvatarUrl;
            lastProcessedUserAvatarRef.current = finalAvatarUrl;
            console.log('✅ [PersonalInfoSettings] Profile image state updated to:', finalAvatarUrl);
          } else {
            console.warn('⚠️ [PersonalInfoSettings] No finalAvatarUrl to set');
            lastLoadedAvatarRef.current = null;
            lastProcessedUserAvatarRef.current = null;
          }
        }
        
        // Clear the local URI since it's now uploaded and saved
        // Only clear if we successfully saved the avatar URL
        if (avatarUrl) {
          console.log('✅ [PersonalInfoSettings] Clearing local image URI, using saved URL:', avatarUrl.substring(0, 80) + '...');
          
          // CRITICAL: Set profileImage FIRST before clearing profileImageUri
          // This ensures the image displays right away without waiting for fresh profile load
          console.log('🔄 [PersonalInfoSettings] Setting profileImage to avatarUrl immediately:', avatarUrl.substring(0, 80) + '...');
          setProfileImage(avatarUrl);
          
          // Update refs to track this as the last loaded/processed avatar
          lastLoadedAvatarRef.current = avatarUrl;
          lastProcessedUserAvatarRef.current = avatarUrl;
          
          // Clear profileImageUri AFTER setting profileImage
          // This prevents the useEffect from interfering
          setProfileImageUri(null);
          
          console.log('✅ [PersonalInfoSettings] ProfileImage set and profileImageUri cleared');
        }
        
        // Load fresh profile data from API to ensure we have the latest data
        // But don't let it override the image we just set
        try {
          console.log('🔄 [PersonalInfoSettings] Loading fresh profile data after save...');
          const freshProfileResponse = await apiService.getUserProfile();
          
          if (freshProfileResponse.statusCode === 1000 && freshProfileResponse.data) {
            const freshData = freshProfileResponse.data;
            const freshAvatarUrl = freshData.avatar || freshData.profilePicture;
            
            console.log('🔄 [PersonalInfoSettings] Fresh profile data loaded:', {
              avatar: freshAvatarUrl ? freshAvatarUrl.substring(0, 50) + '...' : null,
              profilePicture: freshData.profilePicture ? freshData.profilePicture.substring(0, 50) + '...' : null
            });
            
            const normalizedUser = {
              ...freshData,
              id: freshData.id || freshData._id,
              mobile: freshData.mobile || freshData.phone,
              phone: freshData.phone || freshData.mobile,
              avatar: freshAvatarUrl,
              profilePicture: freshData.profilePicture || freshData.avatar || freshAvatarUrl,
              timezone: freshData.timezone,
              timezoneOffset: freshData.timezoneOffset,
            };
            
            // Remove _id if it exists
            if ((normalizedUser as any)._id && normalizedUser.id) {
              delete (normalizedUser as any)._id;
            }
            
            if (setUser) {
              setUser(normalizedUser);
              console.log('✅ [PersonalInfoSettings] Updated user store with fresh profile data after save');
            }
            
            // IMPORTANT: Only update profileImage if it's different and we have a valid URL
            // Don't override the image we just set unless the fresh data has a different (newer) URL
            const finalAvatarUrl = freshAvatarUrl || avatarUrl;
            
            if (finalAvatarUrl && finalAvatarUrl !== profileImage) {
              console.log('🔄 [PersonalInfoSettings] Updating profileImage with fresh avatar URL:', finalAvatarUrl.substring(0, 50) + '...');
              setProfileImage(finalAvatarUrl);
              lastLoadedAvatarRef.current = finalAvatarUrl;
              lastProcessedUserAvatarRef.current = finalAvatarUrl;
              console.log('✅ [PersonalInfoSettings] Updated profileImage state with fresh avatar');
            } else if (finalAvatarUrl && finalAvatarUrl === profileImage) {
              console.log('✅ [PersonalInfoSettings] ProfileImage already matches fresh avatar URL, no update needed');
              lastLoadedAvatarRef.current = finalAvatarUrl;
              lastProcessedUserAvatarRef.current = finalAvatarUrl;
            } else {
              console.warn('⚠️ [PersonalInfoSettings] No valid avatar URL from fresh data or upload');
              lastLoadedAvatarRef.current = null;
              lastProcessedUserAvatarRef.current = null;
            }
          }
        } catch (freshError) {
          console.error('❌ [PersonalInfoSettings] Error loading fresh profile after save:', freshError);
          // Don't fail the save operation if fresh load fails
          // The image should already be set from the upload response
        }
        
        showNotification('success', 'Success', 'Profile updated successfully!');
      } else {
        const errorMsg = (response as any).message || response.errors?.[0]?.message || 'Failed to update profile. Please try again.';
        showNotification('error', 'Error', errorMsg);
      }
    } catch (error: any) {
      console.error('Error updating profile:', error);
      showNotification('error', 'Error', error.message || 'Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
      // Reset saving flag after a delay to allow state updates to complete
      // This prevents the useEffect from interfering with the image we just set
      setTimeout(() => {
        isSavingRef.current = false;
        console.log('✅ [PersonalInfoSettings] Save operation completed, resetting isSavingRef');
      }, 2000); // Increased delay to ensure image state is fully updated
    }
  };

  // Calculate bottom padding to account for bottom tab bar on mobile
  // Bottom tabs are absolutely positioned: minHeight 50 + paddingTop 8 + safe area insets
  // Other screens use 70-100px. Use dynamic calculation: tab height + safe area + extra spacing
  const bottomTabHeight = Platform.OS !== 'web' ? 50 + spacing.sm + insets.bottom : 0;
  const contentContainerStyle = [
    styles.contentContainer,
    Platform.OS !== 'web' && {
      // Ensure save button is fully visible and clickable above bottom tabs
      // Tab height (58) + safe area + button margin (24) + extra spacing (32) = ~114 + safe area
      paddingBottom: bottomTabHeight + spacing.xl + spacing.lg,
    },
  ];

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={contentContainerStyle}
      showsVerticalScrollIndicator={false}
    >
      {Platform.OS !== 'web' && (
        <View style={styles.header}>
          <Text style={styles.title}>Profile & Preferences</Text>
          <Text style={styles.subtitle}>Update your personal information</Text>
        </View>
      )}

      {/* Profile Picture Section */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>Profile Picture</Text>
        <View style={styles.profilePictureContainer}>
          <View style={styles.profilePictureWrapper}>
            {/* Determine if we have a valid image URL */}
            {(() => {
              const hasValidImage = profileImage && 
                                   typeof profileImage === 'string' &&
                                   profileImage.trim() !== '' && 
                                   profileImage !== 'null' && 
                                   profileImage !== 'undefined' &&
                                   (profileImage.startsWith('http://') || 
                                    profileImage.startsWith('https://') || 
                                    profileImage.startsWith('file://') || 
                                    profileImage.startsWith('blob:') || 
                                    profileImage.startsWith('data:'));
              
              // Log rendering decision (throttled for web)
              if (Platform.OS === 'web') {
                const logKey = `img-render-${hasValidImage}-${profileImage?.substring(0, 30)}`;
                if (logKey !== (window as any).__lastImgRenderLog) {
                  console.log('🖼️ [PersonalInfoSettings] Rendering decision (web):', {
                    hasProfileImage: !!profileImage,
                    profileImageValue: profileImage ? profileImage.substring(0, 100) + '...' : 'null',
                    hasValidImage,
                    willShowImage: hasValidImage,
                    willShowPlaceholder: !hasValidImage
                  });
                  (window as any).__lastImgRenderLog = logKey;
                }
              }
              
              // Always render something - either image or placeholder
              if (hasValidImage && profileImage) {
                // Render image
                if (Platform.OS === 'web') {
                  return (
                    <img
                      key={`img-${profileImage}`} // Force re-render when URL changes
                      src={profileImage}
                      alt="Profile picture"
                      style={{
                        width: 80,
                        height: 80,
                        borderRadius: 40,
                        objectFit: 'cover' as const,
                        backgroundColor: (colors.background.secondary as string) || '#f0f0f0',
                        display: 'block',
                        flexShrink: 0,
                      }}
                      onError={(e) => {
                        const target = e.currentTarget;
                        const imageUrl = profileImage || target.src;
                        
                        // Check if we've already attempted to load this exact URL
                        const hasAttempted = imageLoadAttemptedRef.current.has(imageUrl);
                        
                        console.error('❌ [PersonalInfoSettings] Image load error (web):', imageUrl);
                        console.error('❌ [PersonalInfoSettings] Image element state:', {
                          src: target.src,
                          complete: target.complete,
                          naturalWidth: target.naturalWidth,
                          naturalHeight: target.naturalHeight,
                          hasAttempted
                        });
                        
                        // Only clear on first error attempt to prevent infinite loop
                        if (!hasAttempted) {
                          imageLoadAttemptedRef.current.add(imageUrl);
                          console.log('🔄 [PersonalInfoSettings] Image failed to load, clearing to prevent loop');
                          setProfileImage(null);
                          lastLoadedAvatarRef.current = null;
                        } else {
                          console.log('🔄 [PersonalInfoSettings] Image already attempted, preventing further retries');
                        }
                      }}
                      onLoad={(e) => {
                        const target = e.currentTarget;
                        console.log('✅ [PersonalInfoSettings] Image loaded successfully (web) from:', profileImage?.substring(0, 80) + '...');
                        console.log('✅ [PersonalInfoSettings] Image dimensions:', {
                          naturalWidth: target.naturalWidth,
                          naturalHeight: target.naturalHeight,
                          complete: target.complete
                        });
                        // Remove from attempted set on successful load
                        if (profileImage) {
                          imageLoadAttemptedRef.current.delete(profileImage);
                        }
                      }}
                      // Removed crossOrigin to avoid CORS issues - Firebase Storage public URLs with tokens should work without it
                      referrerPolicy="no-referrer"
                    />
                  );
                } else {
                  return (
                    <Image 
                      key={`img-${profileImage}`} // Force re-render when URL changes
                      source={{ uri: profileImage }} 
                      style={styles.profilePicture}
                      onError={(error) => {
                        console.error('❌ [PersonalInfoSettings] Image load error:', error.nativeEvent.error);
                        console.error('❌ [PersonalInfoSettings] Failed to load image from:', profileImage);
                        
                        // If image fails to load and it's a Firebase URL, clear it to prevent infinite retries
                        if (profileImage && (profileImage.includes('firebasestorage.googleapis.com') || profileImage.includes('storage.googleapis.com'))) {
                          console.log('🔄 [PersonalInfoSettings] Firebase image failed to load, clearing to prevent loop');
                          setProfileImage(null);
                          lastLoadedAvatarRef.current = null;
                        }
                      }}
                      onLoad={() => {
                        console.log('✅ [PersonalInfoSettings] Image loaded successfully from:', profileImage?.substring(0, 50) + '...');
                      }}
                    />
                  );
                }
              }
              
              // Show placeholder when no valid image
              if (Platform.OS === 'web' && profileImage && !hasValidImage) {
                console.log('🔄 [PersonalInfoSettings] Showing placeholder - invalid image URL:', profileImage?.substring(0, 50));
              }
              return (
                <View style={styles.profilePicturePlaceholder} key="placeholder">
                  <Ionicons name="person" size={32} color={colors.text.secondary} />
                </View>
              );
            })()}
            <TouchableOpacity
              style={styles.changePictureButton}
              onPress={handlePickImage}
              activeOpacity={0.8}
            >
              <Ionicons name="camera" size={16} color={colors.white} />
            </TouchableOpacity>
          </View>
          <TouchableOpacity
            style={styles.changePictureTextButton}
            onPress={handlePickImage}
            activeOpacity={0.8}
          >
            <Text style={styles.changePictureText}>Change Picture</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Form Section */}
      <View style={styles.section}>
        <Text style={styles.sectionLabel}>Personal Information</Text>

        <Input
          label="Full Name"
          placeholder="Enter your full name"
          value={formData.name}
          onChangeText={(text) => {
            setFormData(prev => ({ ...prev, name: text }));
            if (errors.name) {
              setErrors(prev => ({ ...prev, name: '' }));
            }
          }}
          error={errors.name}
          size="md"
          variant="outlined"
          state={errors.name ? 'error' : 'default'}
          editable={!isLoading}
        />

        <PhoneInput
          ref={phoneInputRef}
          label="Phone Number"
          value={formData.phone}
          onChangeText={(text) => {
            setFormData(prev => ({ ...prev, phone: text }));
            if (errors.phone) {
              setErrors(prev => ({ ...prev, phone: '' }));
            }
          }}
          placeholder="Enter your phone number"
          error={errors.phone}
          size="md"
          variant="outlined"
          state={errors.phone ? 'error' : 'default'}
          disabled={isLoading}
          initialCountry={defaultPhoneCountry()}
        />

        <Input
          label="Email Address"
          placeholder="Enter your email"
          value={formData.email}
          editable={false}
          size="md"
          variant="outlined"
          state="disabled"
        />

        <TimezoneInput
          label="Timezone"
          value={formData.timezone}
          onChange={(timezone, timezoneOffset) => {
            setFormData(prev => ({ ...prev, timezone, timezoneOffset }));
            if (errors.timezone) {
              setErrors(prev => ({ ...prev, timezone: '' }));
            }
          }}
          placeholder="Select your timezone"
          error={errors.timezone}
          size="md"
          variant="outlined"
          state={errors.timezone ? 'error' : 'default'}
          disabled={isLoading}
        />
      </View>

      {/* Save Button */}
      <View style={styles.buttonContainer}>
        <Button
          title="Save Changes"
          onPress={handleSave}
          loading={isLoading}
          variant="primary"
          size="lg"
          fullWidth
        />
      </View>
    </ScrollView>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  contentContainer: {
    padding: spacing.lg,
    paddingBottom: spacing.xl * 2,
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
    paddingVertical: spacing.sm,
  },
  backButtonText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
    marginLeft: spacing.xs,
  },
  header: {
    marginBottom: spacing.xl,
  },
  title: {
    fontSize: typography.fontSize['2xl'],
    fontFamily: typography.fontFamily.bold,
    color: colors.text.primary,
    marginBottom: spacing.xs,
  },
  subtitle: {
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
  },
  section: {
    marginBottom: spacing.xl,
  },
  sectionLabel: {
    fontSize: typography.fontSize.sm,
    textTransform: 'uppercase',
    color: colors.text.secondary,
    letterSpacing: 0.8,
    marginBottom: spacing.md,
    fontFamily: typography.fontFamily.semibold,
  },
  profilePictureContainer: {
    alignItems: 'center',
    paddingVertical: spacing.md,
  },
  profilePictureWrapper: {
    position: 'relative',
    marginBottom: spacing.sm,
  },
  profilePicture: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.background.secondary,
  },
  profilePicturePlaceholder: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.background.secondary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.border,
  },
  changePictureButton: {
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.white,
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.2,
      shadowRadius: 4,
      elevation: 4,
    }),
  },
  changePictureTextButton: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
  },
  changePictureText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  buttonContainer: {
    marginTop: spacing.lg,
  },
});

